import React, { useCallback, useState } from 'react';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import useProtectedMedia from '@jwp/ott-hooks-react/src/useProtectedMedia';

import Modal from '../../components/Modal/Modal';
import Player from '../../components/Player/Player';
import ModalCloseButton from '../../components/ModalCloseButton/ModalCloseButton';
import Fade from '../../components/Animation/Fade/Fade';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import PlayerError, { PlayerErrorState } from '../../components/PlayerError/PlayerError';

import styles from './TrailerModal.module.scss';

type TrailerPlayerProps = {
  item: PlaylistItem;
  onPlay: () => void;
  onPause: () => void;
  onComplete: () => void;
  onUserActive: () => void;
  onUserInActive: () => void;
};

// Re-fetches the trailer through content protection so its sources are signed (url signing / DRM)
const TrailerPlayer: React.FC<TrailerPlayerProps> = ({ item, ...playerProps }) => {
  const { data: playableItem, isLoading, isGeoBlocked } = useProtectedMedia(item);

  if (!playableItem || isLoading) {
    return <LoadingOverlay inline />;
  }

  if (isGeoBlocked) {
    return <PlayerError error={PlayerErrorState.GEO_BLOCKED} />;
  }

  return <Player item={playableItem} {...playerProps} autostart />;
};

type Props = {
  item?: any;
  title: string;
  open: boolean;
  onClose: () => void;
};

const TrailerModal: React.FC<Props> = ({ item, open, title, onClose }) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [userActive, setUserActive] = useState(true);

  const handlePlay = useCallback(() => setIsPlaying(true), []);
  const handlePause = useCallback(() => setIsPlaying(false), []);
  const handleUserActive = useCallback(() => setUserActive(true), []);
  const handleUserInactive = useCallback(() => setUserActive(false), []);

  if (!item) return null;

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="trailer-modal-title" centered>
      <div className={styles.container} role="dialog" aria-modal="true" aria-labelledby="trailer-modal-title">
        {open && (
          <TrailerPlayer
            item={item}
            onPlay={handlePlay}
            onPause={handlePause}
            onComplete={onClose}
            onUserActive={handleUserActive}
            onUserInActive={handleUserInactive}
          />
        )}
        <Fade open={!isPlaying || userActive} keepMounted>
          <div className={styles.playerOverlay}>
            <h1 id="trailer-modal-title" className={styles.title}>
              {title}
            </h1>
          </div>
          <ModalCloseButton onClick={onClose} />
        </Fade>
      </div>
    </Modal>
  );
};

export default TrailerModal;
